import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { formatDate, MOMENT_FORMATS } from '../../../../utils/dateFormat';
import { BRAND_ORANGE } from '../../../../styles/portalTheme';

const formatEventDateTime = (value) =>
  formatDate(value, MOMENT_FORMATS.DATETIME_LONG) || (value ? String(value) : '—');

const formatSubmittedAt = (value) => {
  const datePart = formatDate(value, MOMENT_FORMATS.DATE_MONTH_LONG);
  const timePart = formatDate(value, MOMENT_FORMATS.TIME);
  if (!datePart || !timePart) return value ? String(value) : '—';
  return `Submitted on ${datePart} at ${timePart}`;
};

const DEFAULT_TIMELINE_STEPS = [
  {
    id: 'submitted',
    status: 'completed',
    title: 'Event Submitted',
    description: 'Your event details have been received and queued for review.',
  },
  {
    id: 'moderation',
    status: 'in_progress',
    title: 'Moderation Review',
    statusLabel: 'In Progress',
    description:
      'Our team is reviewing your event against our community guidelines and quality standards.',
  },
  {
    id: 'publishing',
    status: 'pending',
    title: 'Approval & Publishing',
    statusLabel: 'Pending',
    description: 'Once approved, your event will be published and visible to the community.',
  },
];

const mapApiTimelineStep = (step) => ({
  id: step.id ?? step.key,
  status: step.status,
  title: step.title ?? step.label,
  statusLabel: step.statusLabel ?? step.subtitle,
  description: step.description,
  timestamp: step.timestamp ?? step.completedAt ?? step.occurredAt,
});

const buildTimelineSteps = (submittedAt, apiTimeline) => {
  if (apiTimeline?.length) {
    return apiTimeline.map(mapApiTimelineStep);
  }

  return DEFAULT_TIMELINE_STEPS.map((step) =>
    step.id === 'submitted' ? { ...step, timestamp: submittedAt } : step,
  );
};

const SuccessModalHeader = () => (
  <>
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          bgcolor: 'rgba(224, 104, 10, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CheckIcon sx={{ color: BRAND_ORANGE, fontSize: 26 }} />
      </Box>
    </Box>
    <Typography
      align="center"
      sx={{ fontWeight: 700, fontSize: '1.75rem', color: 'text.primary', mb: 1 }}
    >
      Event Submitted Successfully
    </Typography>
    <Typography
      align="center"
      color="text.secondary"
      sx={{ fontSize: '0.9375rem', lineHeight: 1.55, mb: 2.5, px: { xs: 0, sm: 2 } }}
    >
      Your event has been submitted for review by our moderation team. We&apos;ll notify you once
      it&apos;s approved and published.
    </Typography>
  </>
);

const UnderReviewBadge = () => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1.25,
      py: 0.5,
      borderRadius: 9999,
      bgcolor: 'rgba(224, 104, 10, 0.14)',
      color: BRAND_ORANGE,
      fontSize: '0.75rem',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    }}
  >
    <ScheduleOutlinedIcon sx={{ fontSize: 14 }} />
    Under Review
  </Box>
);

const SuccessModalEventSummary = ({ summary }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: (theme) => theme.portal?.accentSoft ?? 'action.hover',
      border: '1px solid',
      borderColor: (theme) => theme.portal?.accentMuted ?? 'divider',
    }}
  >
    <Box
      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.35, color: 'text.primary' }}
        >
          {summary.title || '—'}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.8125rem', mt: 0.5, lineHeight: 1.4 }}>
          {formatSubmittedAt(summary.submittedAt)}
        </Typography>
      </Box>
      <UnderReviewBadge />
    </Box>

    <Divider sx={{ my: 1.5 }} />

    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
      <Box>
        <Typography color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.25 }}>
          Event Date
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.4 }}>
          {formatEventDateTime(summary.eventDate)}
        </Typography>
      </Box>
      <Box>
        <Typography color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.25 }}>
          Venue
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.4 }}>
          {summary.venueName || '—'}
        </Typography>
      </Box>
      <Box>
        <Typography color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.25 }}>
          Capacity
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.4 }}>
          {summary.capacity != null ? `${summary.capacity} attendees` : '—'}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const TimelineStepIcon = ({ status }) => {
  if (status === 'completed') {
    return (
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: '#23B80C33',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <CheckIcon sx={{ color: '#23B80C', fontSize: 20 }} />
      </Box>
    );
  }

  if (status === 'in_progress') {
    return (
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: '#FFC70033',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <HourglassEmptyOutlinedIcon sx={{ color: '#FF8A00', fontSize: 20 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        bgcolor: '#eceff3',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <ScheduleOutlinedIcon sx={{ color: '#9aa3ad', fontSize: 20 }} />
    </Box>
  );
};

const TimelineStep = ({ step, isLast }) => (
  <Box sx={{ display: 'flex', gap: 1.75, position: 'relative', pb: isLast ? 0 : 2.5 }}>
    {!isLast && (
      <Box
        sx={{
          position: 'absolute',
          left: 17,
          top: 40,
          bottom: 0,
          width: 2,
          bgcolor: '#e4e8ec',
        }}
      />
    )}
    <TimelineStepIcon status={step.status} />
    <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
      <Typography
        sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.35, color: 'text.primary' }}
      >
        {step.title}
      </Typography>
      {step.status === 'completed' && step.timestamp && (
        <Typography
          color="text.secondary"
          sx={{ fontSize: '0.8125rem', mt: 0.25, lineHeight: 1.4 }}
        >
          {formatEventDateTime(step.timestamp)}
        </Typography>
      )}
      {step.status !== 'completed' && step.statusLabel && (
        <Typography
          color="text.secondary"
          sx={{ fontSize: '0.8125rem', mt: 0.25, lineHeight: 1.4 }}
        >
          {step.statusLabel}
        </Typography>
      )}
      <Typography color="text.secondary" sx={{ fontSize: '0.8125rem', mt: 0.75, lineHeight: 1.5 }}>
        {step.description}
      </Typography>
    </Box>
  </Box>
);

const SuccessModalTimeline = ({ submittedAt, timeline }) => {
  const steps = buildTimelineSteps(submittedAt, timeline);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 2, color: 'text.primary' }}>
        Review Timeline
      </Typography>
      <Box>
        {steps.map((step, index) => (
          <TimelineStep key={step.id ?? index} step={step} isLast={index === steps.length - 1} />
        ))}
      </Box>
    </Box>
  );
};

const REVIEW_CHECKLIST = [
  'Verification of event details and venue information',
  'Review against community guidelines and quality standards',
  'Content moderation for appropriate language and imagery',
];

const INFO_BLUE = '#1e5a9e';
const INFO_BG = '#f0f7ff';
const INFO_BORDER = '#d6e8f7';

const SuccessModalReviewInfo = () => (
  <Box
    sx={{
      mt: 2.5,
      p: 2,
      borderRadius: 2,
      bgcolor: INFO_BG,
      border: '1px solid',
      borderColor: INFO_BORDER,
    }}
  >
    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: INFO_BLUE, mb: 1 }}>
      What happens during review?
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.25, color: INFO_BLUE }}>
      {REVIEW_CHECKLIST.map((item) => (
        <Typography
          key={item}
          component="li"
          sx={{ fontSize: '0.8125rem', lineHeight: 1.55, mb: 0.5, '&:last-child': { mb: 0 } }}
        >
          {item}
        </Typography>
      ))}
    </Box>

    <Divider sx={{ my: 1.5, borderColor: INFO_BORDER }} />

    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: INFO_BLUE, mb: 0.75 }}>
      Estimated Review Time
    </Typography>
    <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.55, color: INFO_BLUE }}>
      Most events are reviewed within 24–48 hours. You&apos;ll receive an email notification once
      your event is approved or if any changes are needed.
    </Typography>
  </Box>
);

const SuccessModalFooter = ({ onGoToEvents }) => (
  <Button
    variant="contained"
    fullWidth
    onClick={onGoToEvents}
    sx={{
      bgcolor: BRAND_ORANGE,
      textTransform: 'none',
      fontWeight: 700,
      fontSize: '0.9375rem',
      py: 1.35,
      borderRadius: 1.5,
      boxShadow: 'none',
      '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
    }}
  >
    Go to Events
  </Button>
);

const EventSubmissionSuccessModal = ({
  open,
  onGoToEvents,
  eventSummary = {},
  moderation = null,
}) => {
  const summary = eventSummary || {};

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        },
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={onGoToEvents}
        sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary', zIndex: 1 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{
          p: 3.5,
          pb: 2,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <SuccessModalHeader />
        <SuccessModalEventSummary summary={summary} />
        <SuccessModalTimeline submittedAt={summary.submittedAt} timeline={moderation?.timeline} />
        <SuccessModalReviewInfo />
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          px: 3.5,
          py: 2,
          pt: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <SuccessModalFooter onGoToEvents={onGoToEvents} />
      </DialogActions>
    </Dialog>
  );
};

export default EventSubmissionSuccessModal;
